import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { condenseLog } from "@/lib/condenseLog";
import { cn } from "@/lib/utils";
import {
  COMPILED_LINES_TOOLTIP,
  LINES_OF_CODE_TOOLTIP,
  compiledShare,
  fmtLines,
} from "@/lib/codeMetrics";
import { DynamicCodeBadge } from "@/components/DynamicCodeBadge";
import { BuildBadge } from "@/components/BuildBadge";
import { MemoryPanel } from "@/components/MemoryPanel";
import { CachePanel } from "@/components/CachePanel";
import { FailurePanel } from "@/components/FailurePanel";
import { JobJournalPanel } from "@/components/JobJournalPanel";
import { BuildSiblings, useBuildSiblings } from "@/components/BuildSiblings";
import { buildKindOf } from "@/lib/buildLabels";
import {
  ARTIFACTS_DELETED_NOTE,
  isArtifactsDeleted,
  normalizeJobStatus,
  statusChipLabel,
} from "@/lib/jobStatus";

/** Mirrors private/auth's jobsAdminRoutes.ts AdminJobRow exactly (the
 * full row — GET /v1/admin/jobs/:id). */
interface JobRow {
  id: string;
  org_id: string;
  /** Resolved from pure_app's orgs table — see jobsAdminRoutes.ts's
   *  lookupJobIdentities. Null if that lookup itself failed. */
  org_name: string | null;
  api_key_id: string;
  /** The name of the API key the job was submitted with. */
  api_key_name: string | null;
  /** The creator of that key — see lookupJobIdentities's header for why
   *  that's what "who ran it" means here, not a true per-invocation
   *  actor. Null if the key's creator account no longer exists, or the
   *  lookup itself failed. */
  user_id: string | null;
  user_name: string | null;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  source_kind: string;
  entry: string;
  pre_command: string | null;
  /** The build flags this job was actually submitted with (--target,
   *  --libc, --glibc-version, --platform — see internal/cli's build.ts
   *  for the full flag list these come from). Set once at submission,
   *  never updated. Null on a job submitted before this was tracked, or
   *  when the flag was simply left at its default (e.g. libc/target are
   *  effectively always present once the CLI resolves its own defaults
   *  before sending compile.start — null here specifically means "an
   *  older job, before this column existed"). */
  target: string | null;
  libc: string | null;
  glibc_version: string | null;
  platform: string | null;
  job_kind: string | null;
  /** INTERNAL capacity/billing figure — present on the row, deliberately
   *  not displayed (internal/compiler plans/2026-10-12-code-metrics.md). */
  work_units: number | null;
  /** Code metrics (internal/api migrations/0014_jobs_code_metrics.sql);
   *  dynamic_code is SQLite 0/1. */
  total_lines_of_code: number | null;
  compiled_lines_of_code: number | null;
  dynamic_code: number | null;
  binary_s3_key: string | null;
  binary_sha256: string | null;
  binary_bytes: number | null;
  log_s3_key: string | null;
  extras_s3_key: string | null;
  extras_sha256: string | null;
  extras_bytes: number | null;
  error_message: string | null;
  /** Failure verdict + bundle key (internal/api migration 0018; private/auth
   *  AdminJobRow). failure_backfilled = classified on read from the stored log. */
  failure_class?: string | null;
  failure_cause?: string | null;
  bundle_key?: string | null;
  failure_backfilled?: boolean;
  /** Artifacts deleted with a published build (internal/api migration 0020):
   *  the broker serves such a job as status "deleted"; loadJob normalizes it
   *  back to the stored outcome and keeps this flag (lib/jobStatus.ts). */
  outcome?: string | null;
  artifacts_deleted_at?: number | null;
  artifacts_deleted_by?: string | null;
  /** JSON-encoded {file, line, column, source_excerpt} — see private/auth's
   *  jobsAdminRoutes.ts AdminJobRow doc. Parsed below, not consumed raw. */
  error_context: string | null;
  /** Up to the first 1,000 lines of the entry file's OWN source, captured
   *  for EVERY job (not just failures) — see internal/api's migrations/
   *  0004_jobs_entry_snippet.sql. Always shown when present, regardless
   *  of job outcome — unlike error_context above, which is failure-only. */
  entry_snippet: string | null;
  /** Exact @pure/compiler / @pure/interp versions the runner had prewarmed
   *  when it actually built this job — see internal/api's migrations/
   *  0007_jobs_compiler_versions.sql. Null on jobs that predate this
   *  column, or on the COMPILER_NOT_AVAILABLE early-failure path. */
  compiler_version: string | null;
  interp_version: string | null;
  /** cargo build timing summary (JSON-encoded) — see internal/api's
   *  migrations/0008_jobs_cargo_timing_summary.sql. Parsed below, not
   *  consumed raw. Null on failed builds (cargo never finished), jobs
   *  that predate this column, or a compiler build that predates the
   *  [pure-cargo-timing-json] marker line. */
  cargo_timing_summary: string | null;
  /** Split-step PGO / type-profile fields and the build's
   *  self-description (internal/api migrations 0011, 0013, 0015) —
   *  lib/buildLabels.ts turns them into "which build of a publish run". */
  type_profile_collect: number | null;
  pgo_generate: number | null;
  pgo_profile_path: string | null;
  build_kind: string | null;
  build_label: string | null;
  build_seq: number | null;
  build_run: string | null;
  /** Guest memory telemetry (internal/api migration 0017; plan
   *  internal/compiler/plans/2026-10-14-job-memory-telemetry.md): summary
   *  columns + the timeseries JSON string. All null on jobs from a runner
   *  whose guest image predates the sampler. mem_complete is SQLite 0/1. */
  mem_peak_mb: number | null;
  mem_peak_process: string | null;
  mem_peak_process_mb: number | null;
  mem_guest_mb: number | null;
  mem_complete: number | null;
  mem_telemetry: string | null;
  /** Warm-cache summary (internal/api migration 0019; lib/cacheVerdict.ts). */
  cold_unit_count?: number | null;
  unexpected_cold_count?: number | null;
  cold_top?: string | null;
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  updated_at: number;
}

interface ErrorContext {
  file: string;
  line: number;
  column: number;
  source_excerpt: string;
}

function parseErrorContext(raw: string | null): ErrorContext | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ErrorContext;
  } catch {
    return null;
  }
}

/** Mirrors internal/compiler's pipeline-finalize.ts logCargoTimingSummary's
 *  JSON shape (the [pure-cargo-timing-json] marker line), forwarded
 *  verbatim through the runner and jobEventsDO. */
interface CargoTimingSummary {
  totalWallS: number;
  sumUnitS: number;
  parallelism: number;
  serialTailS: number;
  tailUnit: string;
  slowest: Array<{ unit: string; durationS: number; startS: number }>;
}

function parseCargoTimingSummary(
  raw: string | null,
): CargoTimingSummary | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CargoTimingSummary;
  } catch {
    return null;
  }
}

function fmtS(n: number): string {
  return `${n.toFixed(1)}s`;
}

const STATUS_VARIANT: Record<
  JobRow["status"],
  "default" | "secondary" | "destructive" | "success" | "warning"
> = {
  queued: "secondary",
  running: "warning",
  succeeded: "success",
  failed: "destructive",
  cancelled: "secondary",
};

const TERMINAL_STATUSES = new Set(["succeeded", "failed", "cancelled"]);

// Polling, not a push socket: JobEventsDO's only WebSocket surface is the
// CUSTOMER-facing compile socket, gated by a customer API key that's
// org/scope-bound by construction (authenticateOnly requires compile.start
// / source.preload / jobs.subscribe with a validated key) — adapting that
// to an "any job, any org" operator observer would mean meaningfully
// changing a socket that's core to the customer compile-streaming path.
// A 2-3s poll against the plain GET /v1/admin/jobs/:id REST endpoint is
// the lower-risk path and gives effectively-live status for a page an
// operator is actively watching. Stops once the job reaches a terminal
// status — no point polling a job that will never change again.
const POLL_MS = 2_500;

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtTime(ms: number | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString();
}

function fmtBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

function fmtDuration(startMs: number | null, endMs: number | null): string {
  if (!startMs) return "—";
  const end = endMs ?? Date.now();
  const sec = Math.max(0, Math.round((end - startMs) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function LogJobDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [job, setJob] = useState<JobRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The other builds of this job's publish run (empty for a customer job).
  const siblings = useBuildSiblings(token, job?.build_run);
  const [logUrl, setLogUrl] = useState<string | null>(null);
  const [logText, setLogText] = useState<string | null>(null);
  const [logError, setLogError] = useState<string | null>(null);
  // Noise redaction toggle (condensed by default) + scroll-to-bottom —
  // a 31,600-line / 1.8 MB job log (real incident, 2026-09-05: had to be
  // scp'd off the box because it was unusable in the dashboard) is ~85%
  // cargo/npm build-tool list-dump noise. See condenseLog.ts for the
  // exact rules; "full log" always shows the byte-for-byte original.
  const [logView, setLogView] = useState<"condensed" | "full">("condensed");
  const logPreRef = useRef<HTMLPreElement>(null);
  const condensed = useMemo(
    () => (logText !== null ? condenseLog(logText) : null),
    [logText],
  );
  const displayedLog =
    logView === "full" ? logText : (condensed?.text ?? logText);
  const scrollLogToBottom = useCallback(() => {
    const el = logPreRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);
  const [logLoading, setLogLoading] = useState(false);
  const [binaryActionError, setBinaryActionError] = useState<string | null>(
    null,
  );
  const [binaryDownloading, setBinaryDownloading] = useState(false);
  const [binaryDeleting, setBinaryDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  // Re-render every second while a job is running, purely so
  // fmtDuration's "still running" elapsed-time readout ticks forward
  // between polls — doesn't touch job state itself.
  const [, forceTick] = useState(0);

  const loadJob = useCallback(() => {
    call<{ ok: true; job: JobRow }>(`/admin/jobs/${encodeURIComponent(id)}`)
      .then((r) => {
        setJob(normalizeJobStatus(r.job));
        setError(null);
      })
      .catch((err) => setError(errMsg(err, "failed to load job")));
  }, [call, id]);

  useEffect(loadJob, [loadJob]);

  const statusRef = useRef(job?.status);
  statusRef.current = job?.status;
  useEffect(() => {
    const poll = setInterval(() => {
      if (statusRef.current && TERMINAL_STATUSES.has(statusRef.current)) return;
      loadJob();
    }, POLL_MS);
    const tick = setInterval(() => forceTick((n) => n + 1), 1_000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [loadJob]);

  const fetchLog = useCallback(() => {
    if (!job?.log_s3_key) return;
    setLogLoading(true);
    setLogError(null);
    // FIXED 2026-08-24: originally fetched the presigned R2 URL directly
    // from the browser — confirmed live this fails ("failed to load log")
    // because R2 has no CORS policy allowing a cross-origin fetch() from
    // management.pure.dev against r2.cloudflarestorage.com; the presign
    // itself succeeded, only the browser's own follow-up fetch was
    // blocked. /log/content proxies the fetch+gunzip server-side (no CORS
    // restriction on a server-to-server request) and returns plain text,
    // same-origin from this page's point of view via the management
    // proxy. Also fetch /log separately for the "raw .gz" direct-download
    // link — a plain <a href> navigation, not a script fetch, so CORS
    // never applied to that one in the first place.
    Promise.all([
      call<{ ok: true; url: string }>(
        `/admin/jobs/${encodeURIComponent(id)}/log`,
      ),
      call<string>(`/admin/jobs/${encodeURIComponent(id)}/log/content`),
    ])
      .then(([presigned, text]) => {
        setLogUrl(presigned.url);
        setLogText(text);
      })
      .catch((err) => setLogError(errMsg(err, "failed to load log")))
      .finally(() => setLogLoading(false));
  }, [call, id, job?.log_s3_key]);

  const downloadBinary = useCallback(() => {
    setBinaryActionError(null);
    setBinaryDownloading(true);
    call<{ ok: true; url: string }>(
      `/admin/jobs/${encodeURIComponent(id)}/download`,
    )
      .then((r) => {
        // A direct <a href> navigation would need a rendered anchor
        // (like the log's "raw .gz" link); open-in-new-tab on click gets
        // the same one-click "download it" outcome without needing to
        // pre-fetch the URL before the button even renders.
        window.open(r.url, "_blank", "noopener,noreferrer");
      })
      .catch((err) =>
        setBinaryActionError(errMsg(err, "failed to get download URL")),
      )
      .finally(() => setBinaryDownloading(false));
  }, [call, id]);

  const deleteBinary = useCallback(() => {
    if (
      !window.confirm(
        "Delete the compiled binary for this job? This cannot be undone — the customer's download link will stop working immediately.",
      )
    ) {
      return;
    }
    setBinaryActionError(null);
    setBinaryDeleting(true);
    call<{ ok: true }>(`/admin/jobs/${encodeURIComponent(id)}/binary`, {
      method: "DELETE",
    })
      .then(() => loadJob())
      .catch((err) =>
        setBinaryActionError(errMsg(err, "failed to delete binary")),
      )
      .finally(() => setBinaryDeleting(false));
  }, [call, id, loadJob]);

  // "Kill job early" (2026-09-04) — propagates all the way down to the
  // runner actually SIGKILLing the compile process, not just marking the
  // row failed here. Best-effort: a 200 means the cancel request was
  // delivered (or the job was already terminal), not that the process has
  // necessarily stopped yet — loadJob() below picks up the real status
  // once the runner publishes job.cancelled, same lag as any other
  // terminal transition this page already polls for.
  const cancelJob = useCallback(() => {
    if (
      !window.confirm(
        "Kill this job now? This stops the compile process on the runner immediately and cannot be undone.",
      )
    ) {
      return;
    }
    setCancelError(null);
    setCancelling(true);
    call<{ ok: true }>(`/admin/jobs/${encodeURIComponent(id)}/cancel`, {
      method: "POST",
    })
      .then(() => loadJob())
      .catch((err) => setCancelError(errMsg(err, "failed to cancel job")))
      .finally(() => setCancelling(false));
  }, [call, id, loadJob]);

  // "Why is this job just sitting in queued?" (fleet-health-dashboard-
  // plan.md §4.5). The 2026-09-04 incident: a job queued indefinitely
  // because the only runner was DEGRADED (compiler prewarm failing on a
  // missing `unzip`) and nothing on this page could say so. Once a job has
  // been queued past the runner's own 30 s prewarm-retry cadence (2 min
  // here, to avoid flashing on a normal pickup delay), fetch the fleet
  // overview and, if the `compile` capability isn't up, name the reason.
  const [fleetHint, setFleetHint] = useState<{
    capability: string;
    runners: Array<{
      box: string;
      runner: string;
      state: string;
      line: string;
    }>;
  } | null>(null);
  useEffect(() => {
    if (!job || job.status !== "queued") {
      setFleetHint(null);
      return;
    }
    if (Date.now() - job.created_at < 2 * 60_000) return;
    let cancelled = false;
    call<{
      capabilities: Array<{ name: string; state: string; reason: string }>;
      boxes: Array<{
        hostname: string;
        connected: boolean;
        entities: Array<{ entity: string; state: string; detail: unknown }>;
      }>;
    }>("/fleet/overview")
      .then((r) => {
        if (cancelled) return;
        const compile = r.capabilities.find((c) => c.name === "compile");
        if (!compile || compile.state === "up") {
          setFleetHint(null);
          return;
        }
        const runners = r.boxes.flatMap((b) =>
          b.entities
            .filter((e) => e.entity.startsWith("runner:"))
            .map((e) => {
              const d = (e.detail ?? {}) as {
                compiler?: { error?: { code?: string; hint?: string } | null };
                reason?: string;
              };
              const err = d.compiler?.error;
              const line = !b.connected
                ? "deploy-agent silent — box unreachable"
                : err
                  ? `${err.code ?? ""} · ${err.hint ?? ""}`
                  : (d.reason ?? e.state);
              return {
                box: b.hostname,
                runner: e.entity.slice("runner:".length),
                state: b.connected ? e.state : "down",
                line,
              };
            }),
        );
        setFleetHint({
          capability: `${compile.state} — ${compile.reason}`,
          runners,
        });
      })
      .catch(() => {
        /* best-effort — the job page must never break because Fleet is unavailable */
      });
    return () => {
      cancelled = true;
    };
  }, [call, job]);

  // Auto-fetch the log once the job reaches a terminal status and a log is
  // actually available — saves an extra click for the common "job just
  // failed, show me why" flow, without polling the (potentially large)
  // log content itself.
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (
      job &&
      TERMINAL_STATUSES.has(job.status) &&
      job.log_s3_key &&
      !autoFetchedRef.current
    ) {
      autoFetchedRef.current = true;
      fetchLog();
    }
  }, [job, fetchLog]);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Link to="/logs" className="text-sm text-primary hover:underline">
          ← Logs
        </Link>
        <h1 className="text-2xl font-semibold mt-1 font-mono">{id}</h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {job && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <CardTitle>Job status</CardTitle>
                <Badge
                  variant={
                    isArtifactsDeleted(job)
                      ? "outline"
                      : STATUS_VARIANT[job.status]
                  }
                  title={
                    isArtifactsDeleted(job)
                      ? `${ARTIFACTS_DELETED_NOTE} (outcome: ${job.status})`
                      : undefined
                  }
                >
                  {statusChipLabel(job.status, job)}
                </Badge>
                {!TERMINAL_STATUSES.has(job.status) && (
                  <span className="text-xs text-muted-foreground">
                    (auto-updating)
                  </span>
                )}
              </div>
              {!TERMINAL_STATUSES.has(job.status) && (
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={cancelling}
                  onClick={cancelJob}
                >
                  {cancelling ? "killing…" : "Kill job"}
                </Button>
              )}
            </div>
            {isArtifactsDeleted(job) && (
              <p className="text-sm text-muted-foreground">
                {ARTIFACTS_DELETED_NOTE}
                {job.artifacts_deleted_at
                  ? ` Deleted ${fmtTime(job.artifacts_deleted_at)}${job.artifacts_deleted_by ? ` by ${job.artifacts_deleted_by}` : ""}.`
                  : ""}
              </p>
            )}
            {cancelError && (
              <p className="text-sm text-destructive">{cancelError}</p>
            )}
            {fleetHint && (
              <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm space-y-1">
                <div className="font-medium">
                  No healthy runner can take this job — compile capability is{" "}
                  {fleetHint.capability}.
                </div>
                {fleetHint.runners.length === 0 && (
                  <div className="text-muted-foreground">
                    No runner has reported status at all yet.
                  </div>
                )}
                {fleetHint.runners.map((r) => (
                  <div key={`${r.box}:${r.runner}`} className="text-xs">
                    <span className="font-mono">{r.runner}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      on {r.box} —{" "}
                    </span>
                    <span className="uppercase font-medium">{r.state}</span>
                    <span className="text-muted-foreground"> · {r.line}</span>
                  </div>
                ))}
                <div className="text-xs text-muted-foreground">
                  The queue redelivers this job every 30 s until a runner
                  accepts it; it dead-letters after the redelivery budget is
                  exhausted.{" "}
                  <Link to="/fleet" className="text-primary hover:underline">
                    Open Fleet →
                  </Link>
                </div>
              </div>
            )}
            {buildKindOf(job) && (
              <div className="pt-1">
                <BuildBadge job={job} total={siblings?.length} />
              </div>
            )}
            <CardDescription>
              {job.entry} · {job.source_kind} · target{" "}
              {job.binary_s3_key
                ? "binary"
                : job.extras_s3_key
                  ? "node-addon"
                  : "?"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-muted-foreground">Org</dt>
                <dd>
                  {job.org_name ?? (
                    <span className="text-muted-foreground">
                      (name unavailable)
                    </span>
                  )}
                  <div className="font-mono text-xs text-muted-foreground">
                    {job.org_id}
                  </div>
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Ran by</dt>
                <dd>
                  {job.user_name ?? (
                    <span className="text-muted-foreground">
                      {job.user_id
                        ? "(name unavailable)"
                        : "(unknown — key creator not found)"}
                    </span>
                  )}
                  {job.user_id && (
                    <div className="font-mono text-xs text-muted-foreground">
                      {job.user_id}
                    </div>
                  )}
                  {job.api_key_name && (
                    <div className="text-xs text-muted-foreground">
                      via key "{job.api_key_name}"
                    </div>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Created</dt>
                <dd>{fmtTime(job.created_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Started</dt>
                <dd>{fmtTime(job.started_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Duration</dt>
                <dd>{fmtDuration(job.started_at, job.finished_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Finished</dt>
                <dd>{fmtTime(job.finished_at)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Last update</dt>
                <dd>{fmtTime(job.updated_at)}</dd>
              </div>
              {job.binary_bytes !== null && (
                <div>
                  <dt className="text-muted-foreground">Binary size</dt>
                  <dd>{fmtBytes(job.binary_bytes)}</dd>
                </div>
              )}
              {(job.status === "succeeded" ||
                job.total_lines_of_code !== null ||
                job.compiled_lines_of_code !== null ||
                job.dynamic_code !== null) && (
                <>
                  <div>
                    <dt
                      className="text-muted-foreground cursor-help"
                      title={LINES_OF_CODE_TOOLTIP}
                    >
                      Lines of code
                    </dt>
                    <dd className="tabular-nums">
                      {fmtLines(job.total_lines_of_code)}
                    </dd>
                  </div>
                  <div>
                    <dt
                      className="text-muted-foreground cursor-help"
                      title={COMPILED_LINES_TOOLTIP}
                    >
                      Compiled lines of code
                    </dt>
                    <dd className="tabular-nums">
                      {fmtLines(job.compiled_lines_of_code)}
                      {compiledShare(job) && (
                        <span className="text-xs text-muted-foreground ml-1.5">
                          ({compiledShare(job)} of loaded)
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Dynamic code</dt>
                    <dd>
                      <DynamicCodeBadge value={job.dynamic_code} />
                    </dd>
                  </div>
                </>
              )}
            </dl>
            {siblings && <BuildSiblings jobs={siblings} currentId={job.id} />}
            {(job.target ||
              job.libc ||
              job.glibc_version ||
              job.platform ||
              job.job_kind ||
              job.pre_command) && (
              <div>
                <dt className="text-xs text-muted-foreground mb-1">
                  Build flags
                </dt>
                <dd className="flex flex-wrap gap-1.5">
                  {job.job_kind && job.job_kind !== "build" && (
                    <Badge variant="secondary">job_kind: {job.job_kind}</Badge>
                  )}
                  {job.target && (
                    <Badge variant="secondary">--target {job.target}</Badge>
                  )}
                  {job.libc && (
                    <Badge variant="secondary">--libc {job.libc}</Badge>
                  )}
                  {job.glibc_version && (
                    <Badge variant="secondary">
                      --glibc-version {job.glibc_version}
                    </Badge>
                  )}
                  {job.platform && (
                    <Badge variant="secondary">--platform {job.platform}</Badge>
                  )}
                  {!!job.type_profile_collect && (
                    <Badge variant="secondary">--type-profile-collect</Badge>
                  )}
                  {!!job.pgo_generate && (
                    <Badge variant="secondary">--pgo-generate</Badge>
                  )}
                  {job.pgo_profile_path && (
                    <Badge variant="secondary" className="font-mono">
                      --pgo-use {job.pgo_profile_path}
                    </Badge>
                  )}
                  {job.pre_command && (
                    <Badge variant="secondary" className="font-mono">
                      --pre {job.pre_command}
                    </Badge>
                  )}
                </dd>
              </div>
            )}
            {(job.compiler_version || job.interp_version) && (
              <div>
                <dt className="text-xs text-muted-foreground mb-1">
                  Built with
                </dt>
                <dd className="flex flex-wrap gap-1.5">
                  {job.compiler_version && (
                    <Badge variant="secondary" className="font-mono">
                      @pure/compiler {job.compiler_version}
                    </Badge>
                  )}
                  {job.interp_version && (
                    <Badge variant="secondary" className="font-mono">
                      @pure/interp {job.interp_version}
                    </Badge>
                  )}
                </dd>
              </div>
            )}
            {(() => {
              const timing = parseCargoTimingSummary(job.cargo_timing_summary);
              if (!timing) return null;
              return (
                <div className="rounded-md bg-muted/50 border p-3">
                  <div className="text-xs font-semibold text-muted-foreground mb-2">
                    Cargo build timing
                  </div>
                  <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm mb-2">
                    <div>
                      <dt className="text-xs text-muted-foreground">Wall</dt>
                      <dd className="font-mono">{fmtS(timing.totalWallS)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">
                        Parallelism
                      </dt>
                      <dd className="font-mono">
                        {timing.parallelism.toFixed(2)}x
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">
                        Serial tail
                      </dt>
                      <dd className="font-mono">
                        {timing.serialTailS > 1
                          ? `${fmtS(timing.serialTailS)} (${Math.round((timing.serialTailS / timing.totalWallS) * 100)}%)`
                          : "—"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">
                        Sum of unit time
                      </dt>
                      <dd className="font-mono">{fmtS(timing.sumUnitS)}</dd>
                    </div>
                  </dl>
                  {timing.serialTailS > 1 && (
                    <p className="text-xs text-muted-foreground mb-2">
                      <span className="font-mono">{timing.tailUnit}</span> ran
                      alone for {fmtS(timing.serialTailS)} at the end — likely
                      final link / thin-LTO codegen, not something more
                      parallelism can fix.
                    </p>
                  )}
                  {timing.slowest?.length > 0 && (
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="text-muted-foreground text-left">
                          <th className="font-normal pr-3">Slowest units</th>
                          <th className="font-normal pr-3">Duration</th>
                          <th className="font-normal">Started at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timing.slowest.map((u, i) => (
                          <tr key={i} className="font-mono">
                            <td className="pr-3 py-0.5">{u.unit}</td>
                            <td className="pr-3 py-0.5">{fmtS(u.durationS)}</td>
                            <td className="py-0.5">{fmtS(u.startS)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}
            <CachePanel row={job} />
            <MemoryPanel row={job} />
            {job.binary_s3_key && (
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  disabled={binaryDownloading}
                  onClick={downloadBinary}
                  title={
                    job.target === "node-addon"
                      ? "Downloads the artifact exactly as stored, gzip-wrapped (…-node-addon.tar.gz). gunzip once: since 2026-10-13 that gives the addon-set tar (addon-set.json, the main package .tgz and pure-addon-platforms/*.tgz); older builds give the package .tgz."
                      : "Downloads the artifact exactly as stored: gzip-compressed (…-binary.gz). gunzip it to get the executable."
                  }
                >
                  {binaryDownloading
                    ? "preparing…"
                    : job.target === "node-addon"
                      ? "Download addon (.tar.gz)"
                      : "Download binary (.gz)"}
                </Button>
                <Button
                  variant="destructive"
                  disabled={binaryDeleting}
                  onClick={deleteBinary}
                >
                  {binaryDeleting ? "deleting…" : "Delete binary"}
                </Button>
              </div>
            )}
            {binaryActionError && (
              <p className="text-sm text-destructive">{binaryActionError}</p>
            )}
            <div id="failure">
              <FailurePanel jobId={job.id} job={job} />
            </div>
            {job.error_message && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <div className="text-xs font-semibold text-destructive mb-1">
                  Error
                </div>
                <div className="text-sm font-mono">{job.error_message}</div>
              </div>
            )}
            {(() => {
              const ctx = parseErrorContext(job.error_context);
              if (!ctx) return null;
              return (
                <div className="rounded-md bg-muted/50 border p-3">
                  <div className="text-xs font-semibold text-muted-foreground mb-1">
                    Source context — {ctx.file}:{ctx.line}:{ctx.column}
                  </div>
                  {/* ±10 lines of the customer's ORIGINAL source around the
                   * compiler's error, captured by the runner while the
                   * extracted tarball was still on disk (see
                   * internal/runner's extractErrorContext) — reproduce a
                   * build failure without re-requesting the customer's
                   * project. The `>` marker line is the exact error line;
                   * pre-formatted server-side, rendered verbatim here. */}
                  <pre className="text-xs font-mono whitespace-pre overflow-x-auto">
                    {ctx.source_excerpt}
                  </pre>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {job?.entry_snippet && (
        <Card>
          <CardHeader>
            <CardTitle>Entry file source</CardTitle>
            <CardDescription>
              {job.entry} — up to the first 1,000 lines, captured at compile
              time regardless of outcome.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted rounded-md p-3 text-xs overflow-auto max-h-[32rem] whitespace-pre-wrap">
              {job.entry_snippet}
            </pre>
          </CardContent>
        </Card>
      )}

      <JobJournalPanel
        jobId={id}
        jobCreatedAt={job?.created_at ?? null}
        terminal={!!job && TERMINAL_STATUSES.has(job.status)}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Build log</CardTitle>
              <CardDescription>
                Full compiler/cargo output — the same content the runner's own
                journal would show, without needing box access.
                {condensed && (
                  <>
                    {" "}
                    {logView === "condensed"
                      ? `Showing ${condensed.shownLines.toLocaleString()} of ${condensed.totalLines.toLocaleString()} lines — noise redacted (${Math.round((1 - condensed.shownLines / condensed.totalLines) * 100)}%).`
                      : `Showing the full ${condensed.totalLines.toLocaleString()}-line log, unredacted.`}
                  </>
                )}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {logUrl && (
                <a
                  href={logUrl}
                  className="text-sm text-primary hover:underline"
                >
                  raw .gz
                </a>
              )}
              <Button
                variant="secondary"
                disabled={!job?.log_s3_key || logLoading}
                onClick={fetchLog}
              >
                {logLoading ? "loading…" : logText ? "refresh log" : "load log"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!job?.log_s3_key && (
            <p className="text-sm text-muted-foreground">
              {job && isArtifactsDeleted(job)
                ? "Artifacts deleted: this job's log was removed with the published build."
                : job && TERMINAL_STATUSES.has(job.status)
                  ? "No log was uploaded for this job."
                  : "Log not available yet — check back once the job finishes."}
            </p>
          )}
          {logError && <p className="text-sm text-destructive">{logError}</p>}
          {logText && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="inline-flex rounded-md border p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setLogView("condensed")}
                    className={cn(
                      "rounded px-2 py-1 font-medium transition-colors",
                      logView === "condensed"
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Condensed
                  </button>
                  <button
                    type="button"
                    onClick={() => setLogView("full")}
                    className={cn(
                      "rounded px-2 py-1 font-medium transition-colors",
                      logView === "full"
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Full log
                  </button>
                </div>
                <Button variant="outline" size="sm" onClick={scrollLogToBottom}>
                  ↓ Scroll to bottom
                </Button>
              </div>
              <pre
                ref={logPreRef}
                className="bg-muted rounded-md p-3 text-xs overflow-auto max-h-[32rem] whitespace-pre-wrap"
              >
                {displayedLog}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
